/**
 * ChatPanel — floating right-side drawer with employee list + conversation view.
 *
 * Triggered by the ChatIcon (sidebar header). When `isOpen` and no peer
 * selected, shows the user list. When a peer is selected, shows their thread.
 *
 * Polls the active conversation every 5s while open (faster than the global
 * 10s unread poll so messages feel near-instant).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ArrowLeft, Send, Trash2, Loader2,
} from 'lucide-react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESSAGE_POLL_MS = 5_000;

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hh}:${mm}`;
};

const roleChip = (role) => {
  if (role === 'super_admin') return 'bg-primary/10 text-primary';
  return 'bg-surface-100 text-ink/60';
};

const ChatPanel = () => {
  const { user } = useAuth();
  const { isOpen, activePeerId, closePanel, setActivePeerId, refreshUnread, unreadByPeer } = useChat();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [peerInfo, setPeerInfo] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const isAdmin = user?.role === 'super_admin';

  // Load users when panel opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/chat/users`)
      .then((r) => { if (!cancelled) setUsers(r.data.users || []); })
      .catch(() => { if (!cancelled) toast.error('Failed to load users'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const loadConversation = useCallback(async (peerId) => {
    if (!peerId) return;
    try {
      const r = await axios.get(`${API}/chat/conversations/${peerId}`);
      setMessages(r.data.messages || []);
      setPeerInfo(r.data.peer || null);
      // Mark read on view
      await axios.post(`${API}/chat/read/${peerId}`).catch(() => {});
      refreshUnread();
    } catch {
      toast.error('Failed to load messages');
    }
  }, [refreshUnread]);

  // Load + poll active conversation
  useEffect(() => {
    if (!isOpen || !activePeerId) return undefined;
    loadConversation(activePeerId);
    const id = setInterval(() => loadConversation(activePeerId), MESSAGE_POLL_MS);
    return () => clearInterval(id);
  }, [isOpen, activePeerId, loadConversation]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, activePeerId]);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || !activePeerId) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat/send`, { peer_id: activePeerId, content });
      // Optimistic append for instant feedback, then reconcile with server.
      setMessages((prev) => {
        if (prev.some((m) => m.id === r.data.message.id)) return prev;
        return [...prev, r.data.message];
      });
      setDraft('');
      refreshUnread();
      // Reconcile against server so the next polling tick doesn't drop our bubble.
      loadConversation(activePeerId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`);
      loadConversation(activePeerId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="chat-panel"
      className="fixed top-0 right-0 h-screen w-full sm:w-[420px] bg-white border-l border-mute/40 shadow-2xl z-[100] flex flex-col"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-mute/30 flex items-center gap-3">
        {activePeerId ? (
          <>
            <button
              onClick={() => setActivePeerId(null)}
              className="p-1.5 rounded-full hover:bg-surface-100 text-ink/60"
              data-testid="chat-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-ink truncate" data-testid="chat-peer-name">
                {peerInfo?.name || 'Loading…'}
              </div>
              <div className="text-xs text-ink/50 truncate">{peerInfo?.email}</div>
            </div>
          </>
        ) : (
          <div className="flex-1 font-display font-bold text-lg text-ink">Messages</div>
        )}
        <button
          onClick={closePanel}
          aria-label="Close chat"
          className="p-1.5 rounded-full hover:bg-surface-100 text-ink/60"
          data-testid="chat-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {!activePeerId ? (
        <>
          <div className="flex-1 overflow-y-auto" data-testid="chat-user-list">
            {loading && (
              <div className="p-6 text-center text-sm text-ink/40">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            )}
            {!loading && users.length === 0 && (
              <div className="p-8 text-center text-sm text-ink/40">No one else here yet</div>
            )}
            {!loading && users.map((u) => {
              const unread = unreadByPeer[u.id] || u.unread || 0;
              return (
                <button
                  key={u.id}
                  onClick={() => setActivePeerId(u.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-100 transition-colors text-left border-b border-mute/20"
                  data-testid={`chat-user-${u.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-semibold grid place-items-center shrink-0">
                    {(u.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink truncate">{u.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${roleChip(u.role)}`}>
                        {u.role === 'super_admin' ? 'Admin' : 'User'}
                      </span>
                    </div>
                    <div className="text-xs text-ink/50 truncate">{u.email}</div>
                  </div>
                  {unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-sm text-ink/40 py-12">
                No messages yet. Say hi 👋
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user.id;
              const canDelete = isAdmin || mine;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'} group`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm relative ${mine ? 'bg-primary text-white rounded-br-sm' : 'bg-surface-100 text-ink rounded-bl-sm'}`}>
                    <div className="whitespace-pre-wrap break-words">{m.content}</div>
                    <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-ink/40'}`}>
                      {fmtTime(m.created_at)}
                    </div>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(m.id)}
                        title="Delete message"
                        className={`absolute -top-2 ${mine ? '-left-2' : '-right-2'} opacity-0 group-hover:opacity-100 h-6 w-6 grid place-items-center rounded-full bg-white border border-mute/40 text-red-600 hover:bg-red-50 transition-opacity`}
                        data-testid={`chat-delete-${m.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-3 pb-14 border-t border-mute/30 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Type a message…"
              rows={1}
              className="flex-1 resize-none rounded-2xl border border-mute/40 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 max-h-32"
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={sending || !draft.trim()}
              className="h-10 w-10 rounded-full bg-primary text-white grid place-items-center disabled:opacity-40 hover:bg-primary-hover transition-colors shrink-0"
              data-testid="chat-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatPanel;
